import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X } from "lucide-react";
import { BRAND, IMAGES } from "../data/content";

const NAV = [
  { to: "/method", label: "The Method" },
  { to: "/packages", label: "Work With Me" },
  { to: "/about", label: "About" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const lenis = useLenis();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const goHash = (hash) => {
    setOpen(false);
    if (pathname === "/") {
      if (lenis) lenis.scrollTo(hash, { offset: -80, duration: 1.2 });
      else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/" + hash);
    }
  };

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
          scrolled
            ? "bg-brand-cream/90 backdrop-blur-xl shadow-[0_1px_0_rgba(26,51,43,0.08)] py-4"
            : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <img
              src={IMAGES.logo}
              alt="Brighter Tomorrows Holistic Coaching"
              className="h-12 w-12 md:h-14 md:w-14 rounded-full object-cover ring-1 ring-brand-gold/40 group-hover:scale-105 transition-transform duration-500"
            />
            <div className="leading-none hidden sm:block">
              <span className="font-serif text-xl md:text-2xl text-brand-forest tracking-tight">Brighter Tomorrows</span>
              <span className="hidden md:block overline mt-1 text-[0.6rem]">{BRAND.tagline}</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`font-sans text-sm tracking-wide link-underline transition-colors duration-300 ${
                  pathname === n.to ? "text-brand-gold" : "text-brand-forest hover:text-brand-gold"
                }`}
              >
                {n.label}
              </Link>
            ))}
            <button
              data-testid="nav-community"
              onClick={() => goHash("#offers")}
              className="font-sans text-sm tracking-wide link-underline text-brand-forest hover:text-brand-gold transition-colors duration-300"
            >
              Community
            </button>
            <button
              data-testid="nav-cta"
              onClick={() => goHash("#discovery-call")}
              className="font-sans text-sm bg-brand-forest text-brand-cream px-6 py-2.5 rounded-full hover:-translate-y-0.5 hover:shadow-lg transition-transform duration-300"
            >
              Book a Call
            </button>
          </nav>

          <button
            className="md:hidden text-brand-forest"
            onClick={() => setOpen(true)}
            data-testid="nav-menu-open"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-brand-forest text-brand-cream flex flex-col p-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center mb-16">
              <span className="font-serif text-2xl">Brighter Tomorrows</span>
              <button onClick={() => setOpen(false)} data-testid="nav-menu-close" aria-label="Close menu">
                <X size={28} />
              </button>
            </div>
            <nav className="flex flex-col gap-7">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.07 }}
                >
                  <Link to={n.to} className="font-serif text-4xl" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
                    {n.label}
                  </Link>
                </motion.div>
              ))}
              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + NAV.length * 0.07 }}
                onClick={() => goHash("#offers")}
                data-testid="mobile-nav-community"
                className="font-serif text-4xl text-left"
              >
                Community
              </motion.button>
              <button
                onClick={() => goHash("#discovery-call")}
                data-testid="mobile-nav-cta"
                className="mt-6 font-sans text-sm bg-brand-cream text-brand-forest px-6 py-3 rounded-full text-center"
              >
                Book a Call
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

